import * as React from 'react';
import {
	DataGrid,
	GridColDef,
	GridOverlay,
	GridRowParams,
	GridValueGetterParams,
	useGridApiRef,
} from '@mui/x-data-grid';
import { styled } from 'styled-components';
import SelectCategory from '../SelectCategory/SelectCategory';
import {
	Avatar,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	SelectChangeEvent,
	Stack,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { DecQuantityIcon, IncQuantityIcon } from '../Icons';
import { setQueryParamsValues } from '@src/helpers/Helpers';
import NoRowsFound from './NoRowsFound';

const ResponsiveDataGrid = styled(DataGrid)`
	height: 100%;
	& .MuiDataGrid-overlayWrapper {
		height: 100% !important;
	}
`;

const defaultStyles = {
	width: '100%',
	height: 'auto',
	background: 'white',
	color: '#5C5C5C',
	cursor: 'pointer',
	' .MuiDataGrid-cell': {
		border: 'none',
		outline: 'none !important',
	},
	' .MuiDataGrid-columnHeader': {
		border: 'none',
		outline: 'none !important',
	},
	' .MuiDataGrid-row': {
		borderBottom: '1px solid #F1F1F1',
	},
	'.MuiDataGrid-columnHeaders': {
		overflow: 'visible',
		position: 'relative',
		zIndex: 1,
	},
	'.MuiDataGrid-columnHeaderTitleContainer': {
		overflow: 'visible',
	},
	'.MuiDataGrid-columnHeaderTitle': {
		color: 'black',
		fontWeight: 700,
	},
	'.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
		outline: 'none !important',
		border: 'none !important',
	},
};

function CustomNoRowsOverlay({ localText }: any) {
	return (
		<GridOverlay>
			<Stack height="100%" alignItems="center" justifyContent="center">
				{localText}
			</Stack>
		</GridOverlay>
	);
}

function LoadingOverlay() {
	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<div className="chat__body__loadingMore__wrapper">
				<CircularProgress size={28} />
			</div>
		</Box>
	);
}

interface Props {
	rows: any;
	columns: any;
	style?: React.CSSProperties;
	onRowsSelectionHandler: (ids: any) => void;
	handleRowClick: (params: any, event: any) => void;
	showNoRowsFound?: boolean;
	checkboxSelection?: boolean;
	responsiveStyles?: React.CSSProperties;
	muiClassStyles?: any;
	filterOptions?: { id: string; value: string }[];
	loading?: boolean;
	setLoading?: (data: boolean) => void;
	localText?: string;
	setCategoryId?: (data: string) => void;
	setPage?: (data: number) => void;
	filterType?: 'client' | 'server';
	controlRowSelection?: boolean;
	type?: 'client' | 'server';
}

export default function CustomDataGrid2({
	rows = [],
	columns = [],
	style,
	responsiveStyles = {},
	muiClassStyles = {},
	onRowsSelectionHandler,
	handleRowClick,
	showNoRowsFound = false,
	loading = false,
	setLoading,
	checkboxSelection = true,
	localText = 'No rows',
	filterOptions,
	filterType = 'client',
	setCategoryId,
	setPage,
	controlRowSelection,
	type = 'client',
}: Props) {
	const [filterValue, setFilterValue] = React.useState<string>('');
	const [showCategoryFilter, setShowCategoryFilter] = React.useState<boolean>(false);
	const selectCaetgoryButtonRef = React.useRef<HTMLDivElement | null>(null);
	const gridApiRef = useGridApiRef();

	const handleCategoryFilterButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		e.preventDefault();
		setShowCategoryFilter(!showCategoryFilter);
	};

	const handleChange = (event: SelectChangeEvent<unknown>) => {
		const selectedValue = event.target.value as string;
		if (filterType === 'client') {
			setFilterValue(selectedValue);
		} else {
			if (setCategoryId && setPage) {
				setCategoryId(selectedValue);
				setPage(1);
				setQueryParamsValues({ page: 1, limit: 25 });

				if (setLoading) {
					setLoading(true);
				}
			}
			setFilterValue(selectedValue);
		}
	};

	const handleClose = () => {
		setShowCategoryFilter(false);
	};

	const customColumns = columns?.map((col: any) => {
		if (col?.field === 'category') {
			return {
				...col,
				renderHeader: (params: any) => (
					<div
						ref={selectCaetgoryButtonRef}
						style={{
							display: 'flex',
							alignItems: 'center',
							color: 'black',
							fontWeight: '700',
							overflow: 'visible',
						}}
					>
						{params.colDef.headerName}
						<IconButton
							size="small"
							style={{ marginRight: 'auto', position: 'relative', overflow: 'visible' }}
							onClick={handleCategoryFilterButtonClick}
						>
							<TuneIcon sx={{ height: '16px', width: '16px' }} />
						</IconButton>
					</div>
				),
			};
		} else if (col?.field === 'recommended') {
			return {
				...col,
				renderCell: (params: any) => (
					<Box
						sx={{
							alignItems: 'center',
							background: '#EDEDED',
							width: '100%',
							height: '38px',
							display: 'flex',
							borderRadius: '4px',
							justifyContent: 'center',
						}}
					>
						{params.value}
					</Box>
				),
			};
		}
		return col;
	});

	const handleClear = () => {
		if (filterType === 'client') {
			setFilterValue('');
			if (setCategoryId) {
				setCategoryId('');
			}
		} else {
			setFilterValue('');
			if (setCategoryId && setPage && setLoading) {
				setCategoryId('');
				setPage(1);
				setLoading(true);
			}
		}
	};

	const getRows = () => {
		if (filterType === 'client') {
			return filterValue ? rows?.filter(({ categoryId }: any) => categoryId === filterValue) : rows;
		}
		return rows;
	};

	React.useEffect(() => {
		// Scroll to the top of the DataGrid when the rows change
		const virtualScroller = document?.querySelector('.MuiDataGrid-virtualScroller');
		if (virtualScroller) {
			virtualScroller.scrollTop = 0;
		}
	}, [rows]);

	const dataGridVariants = () => {
		if (controlRowSelection) {
			return (
				<ResponsiveDataGrid
					apiRef={gridApiRef}
					sx={{ ...defaultStyles, ...style, ...responsiveStyles, ...muiClassStyles }}
					disableColumnMenu={true}
					rows={getRows()}
					columns={customColumns}
					onRowSelectionModelChange={(ids: any) => onRowsSelectionHandler(ids)}
					onRowClick={(params, event) => handleRowClick(params, event)}
					isRowSelectable={(params: GridRowParams) => (params?.row?.disableRow ? false : true)}
					disableRowSelectionOnClick
					hideFooterPagination={true}
					hideFooter={true}
					checkboxSelection={checkboxSelection}
					rowSelectionModel={React.useMemo(
						() => [...rows]?.filter((row) => row?.selected)?.map((row) => row?.id),
						[rows]
					)}
					localeText={{
						noRowsLabel: localText,
					}}
					loading={loading}
				/>
			);
		} else if (type === 'client') {
			return (
				<ResponsiveDataGrid
					apiRef={gridApiRef}
					sx={{ ...defaultStyles, ...style, ...responsiveStyles, ...muiClassStyles }}
					disableColumnMenu={true}
					rows={getRows()}
					columns={customColumns}
					onRowSelectionModelChange={(ids: any) => onRowsSelectionHandler(ids)}
					onRowClick={(params, event) => handleRowClick(params, event)}
					isRowSelectable={(params: GridRowParams) => (params?.row?.disableRow ? false : true)}
					disableRowSelectionOnClick
					hideFooterPagination={rows?.length <= 100}
					hideFooter={rows?.length <= 100}
					checkboxSelection={checkboxSelection}
					localeText={{
						noRowsLabel: localText,
					}}
					loading={loading}
					initialState={{
						pagination: {
							paginationModel: {
								pageSize: 100,
							},
						},
					}}
					pageSizeOptions={[100]}
				/>
			);
		}
		return (
			<ResponsiveDataGrid
				apiRef={gridApiRef}
				sx={{ ...defaultStyles, ...style, ...responsiveStyles, ...muiClassStyles }}
				disableColumnMenu={true}
				rows={getRows()}
				columns={customColumns}
				onRowSelectionModelChange={(ids: any) => onRowsSelectionHandler(ids)}
				onRowClick={(params, event) => handleRowClick(params, event)}
				isRowSelectable={(params: GridRowParams) => (params?.row?.disableRow ? false : true)}
				disableRowSelectionOnClick
				hideFooterPagination
				hideFooter
				checkboxSelection={checkboxSelection}
				localeText={{
					noRowsLabel: localText,
				}}
				loading={loading}
			/>
		);
	};

	return (
		<>
			{/* <NoRowsFound rows={rows} /> */}
			<SelectCategory
				top={selectCaetgoryButtonRef.current?.getBoundingClientRect()?.top ?? 0}
				left={selectCaetgoryButtonRef.current?.getBoundingClientRect()?.left ?? 0}
				isOpen={showCategoryFilter}
				setFilterValue={setFilterValue}
				handleChange={handleChange}
				handleClose={handleClose}
				handleClear={handleClear}
				filterValue={filterValue}
				filterOptions={filterOptions}
			/>
			{rows?.every((row: any) => row?.id)
				? dataGridVariants()
				: null}
		</>
	);
}
